import { ChevronDown, ChevronUp, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePurchaseOrderMainComments } from '@/features/purchase-order/hooks/use-purchase-order-main-comments';
import { PurchaseOrderMainComment } from '@/features/purchase-order/purchase-order-main-comment';
import { PurchaseOrderMainCommentCreate } from '@/features/purchase-order/purchase-order-main-comment-create';
import { PurchaseOrderMainCommentDelete } from '@/features/purchase-order/purchase-order-main-comment-delete';
import { PurchaseOrderMainCommentEdit } from '@/features/purchase-order/purchase-order-main-comment-edit';

type PurchaseOrderMainCommentsProps = {
  purchaseOrderId: string;
};

export function PurchaseOrderMainComments({
  purchaseOrderId,
}: PurchaseOrderMainCommentsProps) {
  const { t } = useTranslation('purchaseOrder');

  const {
    purchaseOrder,
    openCreate,
    setOpenCreate,
    openEdit,
    setOpenEdit,
    openDelete,
    setOpenDelete,
    selectedComment,
    openCreateDialog,
    openEditDialog,
    openDeleteDialog,
  } = usePurchaseOrderMainComments({ purchaseOrderId });

  const [expanded, setExpanded] = useState(false);
  const COMMENTS_LIMIT = 5;
  const sortedComments = [...(purchaseOrder?.comments ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const displayedComments = expanded
    ? sortedComments
    : sortedComments.slice(0, COMMENTS_LIMIT);
  const hasMore = sortedComments.length > COMMENTS_LIMIT;

  return (
    <>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-2'>
            <CardTitle>{t('cardPurchaseOrderComments')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className='space-y-3'>
          {(purchaseOrder?.comments?.length ?? 0) === 0 && (
            <div className='text-muted-foreground text-sm'>
              {t('noComment')}
            </div>
          )}
          {displayedComments.map((comment) => (
            <PurchaseOrderMainComment
              comment={comment}
              key={comment.id}
              openDeleteDialog={openDeleteDialog}
              openEditDialog={openEditDialog}
            />
          ))}
          {hasMore && (
            <Button
              className='w-full'
              onClick={() => setExpanded((e) => !e)}
              size='sm'
              type='button'
              variant='ghost'
            >
              {expanded ? (
                <>
                  <ChevronUp className='size-4' />
                  {t('showLess', { ns: 'common' })}
                </>
              ) : (
                <>
                  <ChevronDown className='size-4' />
                  {t('showMore', { ns: 'common' })}
                </>
              )}
            </Button>
          )}
          <div className='flex justify-center'>
            <Button
              onClick={openCreateDialog}
              size='sm'
              type='button'
              variant='outline'
            >
              <PlusIcon className='size-4' />
              {t('Create comment')}
            </Button>
          </div>
        </CardContent>
      </Card>
      <PurchaseOrderMainCommentCreate
        open={openCreate}
        purchaseOrderId={purchaseOrderId}
        setOpen={setOpenCreate}
      />
      {selectedComment && (
        <>
          <PurchaseOrderMainCommentEdit
            comment={selectedComment}
            open={openEdit}
            purchaseOrderId={purchaseOrderId}
            setOpen={setOpenEdit}
          />
          <PurchaseOrderMainCommentDelete
            commentId={selectedComment.id}
            open={openDelete}
            purchaseOrderId={purchaseOrderId}
            setOpen={setOpenDelete}
          />
        </>
      )}
    </>
  );
}
